<template>
    <div>
        <v-data-table
            :headers="headers"
            :items="filterRequests"
            item-key="item.id"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading || initialLoad"
            :height="'70vh'"
            fixed-header
            :footer-props="{
                itemsPerPageOptions: [15, 30, 100, -1],
                itemsPerPageText: 'Items Per Page',
            }"
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="align-center py-4 mx-0"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col
                        cols="12"
                        class="d-flex align-center pl-sm-8 pl-md-2 pl-lg-3 pl-xl-3 pb-2"
                    >
                        <h3 class="text-h6 font-weight-bold text-primary">
                            HISTORY
                        </h3>
                    </v-col>
                    <v-col
                        cols="12"
                        class="d-flex justify-space-between align-center pl-sm-8 pl-md-2 pl-lg-3 pl-xl-3 pt-2"
                    >
                        <!-- Container for Date Range, Single Date and Search aligned -->
                        <div
                            class="d-flex align-end"
                            style="gap: 16px; align-items: flex-end;"
                        >
                            <!-- Date Range -->
                            <v-menu
                                ref="menu"
                                v-model="menu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                @input="onMenuOpen"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="dateRangeText"
                                        label="Date Range (Max 3 months)"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        :placeholder="
                                            dates &&
                                            dates.length === 2 &&
                                            dates[0] &&
                                            dates[1]
                                                ? ''
                                                : 'Select date range (Max 3 months)'
                                        "
                                        :disabled="loading"
                                        dense
                                        outlined
                                        hide-details
                                        class="compact-date-field"
                                        style="min-width: 320px;"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="tempDates"
                                    range
                                    scrollable
                                    :allowed-dates="allowedDates"
                                    @input="onDateRangeInput"
                                >
                                    <v-spacer></v-spacer>
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="cancelDateRange"
                                    >
                                        Cancel
                                    </v-btn>
                                    <v-btn
                                        text
                                        color="primary"
                                        :loading="loading"
                                        :disabled="!isValidDateRange"
                                        @click="confirmDateRange"
                                    >
                                        OK
                                    </v-btn>
                                </v-date-picker>
                            </v-menu>

                            <!-- Single Date Picker -->
                            <v-menu
                                ref="singleDateMenu"
                                v-model="singleDateMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="singleDateText"
                                        label="Single Date"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        placeholder="Select a date"
                                        :disabled="loading"
                                        dense
                                        outlined
                                        hide-details
                                        class="compact-date-field"
                                        style="min-width: 150px;"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="singleDate"
                                    scrollable
                                    @input="onSingleDateSelected"
                                >
                                </v-date-picker>
                            </v-menu>

                            <!-- Search Field -->
                            <div class="search-field-container">
                                <v-text-field
                                    v-model="unifiedSearch"
                                    label="Search (ID, Project, Item, Order, Supplier, Invoices,...)"
                                    placeholder="text to search (No dates allowed)"
                                    dense
                                    outlined
                                    clearable
                                    :disabled="loading"
                                    class="unified-search-field"
                                    hide-details="auto"
                                    @keyup.enter="performUnifiedSearch"
                                    @click:clear="clearUnifiedSearch"
                                    :error="isDateInput(unifiedSearch)"
                                    :error-messages="
                                        isDateInput(unifiedSearch)
                                            ? 'Date searches not allowed. Use Date Range picker instead.'
                                            : []
                                    "
                                    style="width: 100%;"
                                >
                                    <template v-slot:append>
                                        <div class="d-flex align-center">
                                            <!-- Search button (magnifying glass) -->
                                            <v-btn
                                                icon
                                                small
                                                color="primary"
                                                :loading="loading"
                                                :disabled="
                                                    !unifiedSearch ||
                                                        unifiedSearch.trim() ===
                                                            '' ||
                                                        isDateInput(
                                                            unifiedSearch
                                                        )
                                                "
                                                @click="performUnifiedSearch"
                                                class="mr-1"
                                            >
                                            </v-btn>
                                        </div>
                                    </template>
                                </v-text-field>
                            </div>

                            <!-- Search Button -->
                            <v-btn
                                rounded
                                color="primary"
                                @click="performUnifiedSearch"
                                :disabled="
                                    !unifiedSearch ||
                                        unifiedSearch.trim() === '' ||
                                        isDateInput(unifiedSearch) ||
                                        loading
                                "
                                :loading="loading"
                                class="search-button"
                            >
                                <v-icon left>mdi-magnify</v-icon>
                                search
                            </v-btn>

                            <!-- Reset Button -->
                            <v-btn
                                rounded
                                color="grey darken-1"
                                dark
                                @click="resetAllFilters"
                                :disabled="loading"
                                :loading="loading"
                                class="reset-button"
                            >
                                <v-icon left>mdi-refresh</v-icon>
                                Reset
                            </v-btn>
                        </div>

                        <!-- Report Button -->
                        <v-btn
                            rounded
                            color="primary"
                            @click="exportToExcel"
                            :disabled="loading || filterRequests.length === 0"
                            :loading="exportLoading"
                        >
                            <v-icon left>mdi-file-document</v-icon>
                            Report
                        </v-btn>
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.requestNumber`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="idToFilter"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.type`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="typeToFilter"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.project`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="projectToFilter"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.items`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="itemsToFilter"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.purchaseOrder.orderNumber`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="orderToFilter"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.supplier`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="supplierToFilter"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.invoices`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="invoicesToFilter"
                    placeholder="Search by consecutive, filename or order number"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.invoiceIssueDate`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="issueDateToFilter"
                    placeholder="Search by issue date"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.invoiceDueDate`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="dueDateToFilter"
                    placeholder="Search by due date"
                    dense
                    class="pt-2"
                />
            </template>

            <!--ITEMS-->
            <template v-slot:[`item.requestNumber`]="{ item }">
                <p class="my-0">
                    {{ item.requestNumber }}
                </p>
            </template>
            <template v-slot:[`item.type`]="{ item }">
                <p class="my-0">
                    {{ item.type || 'N/A' }}
                </p>
            </template>
            <template v-slot:[`item.id`]="{ item }">
                <p class="my-0">
                    {{ item.id }}
                </p>
            </template>

            <template v-slot:[`item.createdBy`]="{ item }">
                <div class="my-0">
                    <div>
                        <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                                <p
                                    class="my-0 font-weight-medium"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ item.createdBy.name }}
                                </p>
                            </template>
                            <span>Creator</span>
                        </v-tooltip>
                    </div>
                    <div v-if="item.projectManager">
                        <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                                <p
                                    class="my-0 font-weight-medium"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ item.projectManager.name }}
                                </p>
                            </template>
                            <span>Project Manager</span>
                        </v-tooltip>
                    </div>
                    <div v-if="item.buyer">
                        <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                                <p
                                    class="my-0 font-weight-medium"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ getUser(item.buyer) }}
                                </p>
                            </template>
                            <span>Buyer</span>
                        </v-tooltip>
                    </div>
                </div>
            </template>
            <template v-slot:[`item.items`]="{ item }">
                <div>
                    <p class="my-0">
                        {{ item.description }}
                    </p>
                </div>
            </template>

            <template v-slot:[`item.quantity`]="{ item }">
                <div class="d-flex align-center justify-center">
                    <div
                        class="quantity-container d-flex align-center"
                        :style="{
                            backgroundColor:
                                getQuantityStatusColor(item) === 'green'
                                    ? '#4caf50'
                                    : getQuantityStatusColor(item) === 'orange'
                                    ? '#ff9800'
                                    : '#f44336',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                        }"
                    >
                        {{ item.totalReceived }} /
                        {{ item.quantity }}
                    </div>
                </div>
            </template>
            <template v-slot:[`item.purchaseOrder.orderNumber`]="{ item }">
                <div>
                    <p class="my-0">
                        {{
                            item.purchaseOrder
                                ? item.purchaseOrder.orderNumber
                                : "doesn't have orderNumber"
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.supplier`]="{ item }">
                <div>
                    <p class="my-0">
                        {{ getSupplierName(item) }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.invoices`]="{ item }">
                <div v-if="getFilteredInvoices(item).length > 0">
                    <div
                        v-for="(invoice, index) in getFilteredInvoices(item)"
                        :key="index"
                        class="invoice-item-row"
                    >
                        <p
                            v-if="invoice.fileName || invoice.consecutive"
                            @click="openInvoice(invoice, item)"
                            class="my-2 invoice-filename"
                            :style="{
                                'font-size': '12px',
                                color:
                                    invoice.creditNote ||
                                    invoice.rejected ||
                                    invoice.legalName === false ||
                                    invoice.nit === false
                                        ? '#f44336'
                                        : '#2b81d6',
                                'text-decoration': 'underline',
                                cursor: 'pointer',
                            }"
                        >
                            {{
                                truncateInvoiceText(
                                    invoice.consecutive || invoice.fileName
                                )
                            }}
                        </p>
                        <p v-else class="my-2">doesn't have files</p>
                    </div>
                </div>
                <div v-else>
                    <p class="my-0">doesn't have invoices</p>
                </div>
            </template>
            <template v-slot:[`item.invoiceIssueDate`]="{ item }">
                <div v-if="getFilteredInvoices(item).length > 0">
                    <div
                        v-for="(invoice, index) in getFilteredInvoices(item)"
                        :key="index"
                        class="invoice-item-row"
                    >
                        <p
                            class="my-2 invoice-date"
                            :style="{
                                color:
                                    invoice.creditNote || invoice.rejected
                                        ? '#f44336'
                                        : 'inherit',
                            }"
                        >
                            {{
                                invoice.invoicesDate
                                    ? invoice.invoicesDate.split('T')[0]
                                    : ' - '
                            }}
                        </p>
                    </div>
                </div>
                <div v-else>
                    <p class="my-0">-</p>
                </div>
            </template>
            <template v-slot:[`item.invoiceDueDate`]="{ item }">
                <div v-if="getFilteredInvoices(item).length > 0">
                    <div
                        v-for="(invoice, index) in getFilteredInvoices(item)"
                        :key="index"
                        class="invoice-item-row"
                    >
                        <p
                            class="my-2 invoice-date"
                            :style="{
                                color:
                                    invoice.creditNote || invoice.rejected
                                        ? '#f44336'
                                        : 'inherit',
                            }"
                        >
                            {{ invoice.dueDate || ' - ' }}
                        </p>
                    </div>
                </div>
                <div v-else>
                    <p class="my-0">-</p>
                </div>
            </template>
            <template v-slot:[`item.totalPrice`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ formatNumber(calculateItemTotal(item)) }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.project`]="{ item }">
                <p class="my-0 text-capitalize">{{ item.project.name }}</p>
            </template>
            <template v-slot:[`item.accountant`]="{ item }">
                <div class="d-flex align-center justify-center">
                    <v-checkbox
                        v-model="item.accountant"
                        disabled
                        color="primary"
                        hide-details
                        class="my-0"
                    />
                </div>
            </template>
            <template v-slot:[`item.sap`]="{ item }">
                <div class="d-flex align-center justify-center">
                    <v-checkbox
                        v-model="item.sap"
                        disabled
                        color="success"
                        hide-details
                        class="my-0"
                    />
                </div>
            </template>
        </v-data-table>

        <!--ALERT MESSAGES-->
        <v-alert
            v-if="activateAlert"
            type="success"
            color="primary"
            :style="{ position: 'absolute', right: '0px', bottom: '0px' }"
        >
            <v-row no-gutters>
                <v-col cols="11">
                    <p class="my-0">{{ alertMessage }}</p>
                </v-col>
                <v-col cols="1">
                    <v-btn
                        small
                        icon
                        class="mt-n1"
                        @click="activateAlert = false"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
        <!--Request Form-->
        <v-dialog persistent fullscreen v-model="openForm">
            <PurchaseRequest
                v-if="openForm"
                :localRequest="selectedRequest"
                :originalRequest="originalRequest"
                :createForm="createForm"
                @closeDialog="closeRequestForm"
                @replaceRequest="replaceRequest"
                @closePurchase="closePurchase"
            />
        </v-dialog>
    </div>
</template>

<script>
import PurchaseRequest from '@/components/Purchasing/PurchaseRequest.vue'
import moment from 'moment'
import { mapMutations, mapGetters, mapState, mapActions } from 'vuex'
import API from '@/services/api'
import { openFile } from '@/services/storage/openFile.js'
import { exportPurchaseDataToExcel } from '@/utils/excelExport.js'
import { firestore } from '@/services/firebase'

export default {
    name: 'AccountantHistory',

    components: {
        PurchaseRequest,
    },
    data: () => ({
        createForm: false,
        selectedRequest: {},
        originalRequest: {},
        openForm: false,
        activateAlert: false,
        alertMessage: '',
        loading: false,
        initialLoad: true,
        exportLoading: false, // Loading state specific for Excel export

        // Date filters
        dates: [],
        tempDates: [], // Temporary dates before confirmation
        menu: false,

        // Single date picker
        singleDate: null,
        singleDateMenu: false,
        headers: [
            {
                text: 'ID',
                value: 'requestNumber',
                sortable: false,
                align: 'center',
                width: '100px',
            },
            {
                text: 'TYPE',
                value: 'type',
                sortable: false,
                align: 'left',
                width: '130px',
            },
            {
                text: 'PROJECT',
                value: 'project',
                sortable: false,
                align: 'left',
            },
            {
                text: 'ITEM',
                value: 'items',
                sortable: false,
                align: 'left',
            },
            {
                text: 'RECEIVED / QTY',
                value: 'quantity',
                sortable: false,
                align: 'center',
                width: '130PX',
            },
            {
                text: 'ORDER',
                value: 'purchaseOrder.orderNumber',
                sortable: false,
                align: 'center',
            },
            {
                text: 'TOTAL PRICE',
                value: 'totalPrice',
                sortable: false,
                align: 'center',
            },
            {
                text: 'SUPPLIER',
                value: 'supplier',
                sortable: false,
                align: 'center',
            },
            {
                text: 'INVOICES',
                value: 'invoices',
                sortable: false,
                align: 'left',
                width: '80px',
            },
            {
                text: 'ISSUE DATE',
                value: 'invoiceIssueDate',
                sortable: false,
                align: 'center',
                width: '130px',
            },
            {
                text: 'DUE DATE',
                value: 'invoiceDueDate',
                sortable: false,
                align: 'center',
                width: '130px',
            },
            {
                text: 'SAP',
                value: 'sap',
                sortable: true,
                align: 'center',
                width: '100px',
            },
            {
                text: 'ACCOUNTANT',
                value: 'accountant',
                sortable: true,
                align: 'center',
                width: '150px',
            },
        ],
        projectToFilter: undefined,
        itemsToFilter: undefined,
        orderToFilter: undefined,
        supplierToFilter: undefined,
        invoicesToFilter: undefined,
        issueDateToFilter: undefined,
        dueDateToFilter: undefined,
        typeToFilter: undefined,
        closedPurchases: [],

        companyId: JSON.parse(localStorage.getItem('company')),
        listener: null,
        idToFilter: undefined,
        resourceId: undefined,
        users: [],
        checkAccountantPurchasesPermission: undefined,

        // Unified search
        unifiedSearch: '',
        isUnifiedSearchActive: false, // Flag to know if we are in unified search mode

        allRequests: [], // All requests from Firebase

        // Component lifecycle flag
        isComponentActive: true,
    }),
    computed: {
        ...mapState(['notificationResource']),
        dateRangeText() {
            if (this.dates && this.dates.length === 2) {
                return this.dates.join(' ~ ')
            }
            return ''
        },
        isValidDateRange() {
            if (
                !this.tempDates ||
                this.tempDates.length !== 2 ||
                !this.tempDates[0] ||
                !this.tempDates[1]
            ) {
                return false
            }

            const startDate = new Date(this.tempDates[0])
            const endDate = new Date(this.tempDates[1])

            // Check if end date is after start date
            if (endDate < startDate) {
                return false
            }

            // Allow maximum 3 months (approximately 93 days)
            const daysDiff = Math.ceil(
                (endDate - startDate) / (1000 * 60 * 60 * 24)
            )
            const maxDays = 93 // Approximately 3 months (31 * 3)

            return daysDiff <= maxDays
        },
        singleDateText() {
            if (this.singleDate) {
                return this.singleDate
            }
            return ''
        },
        // Unified array of all purchases
        allPurchases() {
            return [...this.allRequests, ...this.closedPurchases]
        },
        itemsForTable() {
            let allItems = []
            this.allPurchases.forEach(request => {
                if (request.items && request.items.length > 0) {
                    request.items.forEach(item => {
                        // get item invoices
                        const itemInvoices = request.invoices?.filter(invoice =>
                            invoice.relatedEntries?.some(
                                entry => entry.itemId === item.id
                            )
                        ).map(invoice => ({
                            ...invoice,
                            invoicesDate: invoice.invoiceDate || invoice.invoicesDate,
                        }))

                        const newItem = {
                            ...item,
                            requestId: request.id,
                            requestNumber: request.number,
                            requestStatus: request.status,
                            project: request.project,
                            createdBy: request.createdBy,
                            projectManager: request.projectManager,
                            buyer: request.buyer,
                            accountantChecked: item.accountant || false,
                            accountantLoading: false,
                            returnLoading: false,
                            createdOn: request.createdOn,
                            dueDate: request.dueDate,
                            importation: request.importation,
                            type: request.type,
                            notes: request.notes,
                            deliveryLocation: request.deliveryLocation,
                            totalRequestPrice: this.calculateTotal(request),
                            sapPercentage: request.sapPercentage,
                            requestInvoices: request.invoices || [],
                            totalReceived: this.getTotalReceivedQuantity(item),
                            invoices: itemInvoices || [],
                        }
                        // get sap and accountant values
                        newItem.sap = this.getItemSapStatus(newItem)
                        newItem.accountant = this.getItemAccountantStatus(
                            newItem
                        )
                        // add to all items
                        allItems.push(newItem)
                    })
                }
            })
            return allItems
        },
        filterRequests() {
            this.openResource()
            let conditions = []

            // Date range filter (applied first to reduce dataset)
            if (
                this.dates &&
                this.dates.length === 2 &&
                this.dates[0] &&
                this.dates[1] &&
                !this.isUnifiedSearchActive &&
                !this.singleDate
            ) {
                conditions.push(this.filterForDateRange)
            }

            // Single date filter
            if (this.singleDate && !this.isUnifiedSearchActive) {
                conditions.push(this.filterForSingleDate)
            }

            // Individual filters (kept for compatibility)
            if (this.idToFilter) {
                conditions.push(this.filterForId)
            }

            if (this.projectToFilter) {
                conditions.push(this.filterForProject)
            }

            if (this.itemsToFilter) {
                conditions.push(this.filterForItems)
            }

            if (this.orderToFilter) {
                conditions.push(this.filterForOrder)
            }

            if (this.supplierToFilter) {
                conditions.push(this.filterForSupplier)
            }

            if (this.invoicesToFilter) {
                conditions.push(this.filterForInvoices)
            }

            if (this.issueDateToFilter) {
                conditions.push(this.filterForIssueDate)
            }

            if (this.dueDateToFilter) {
                conditions.push(this.filterForDueDate)
            }

            if (this.typeToFilter) {
                conditions.push(this.filterForType)
            }

            // If we are in unified search mode, apply unified search filter
            if (
                this.isUnifiedSearchActive &&
                this.unifiedSearch &&
                this.unifiedSearch.trim()
            ) {
                this.sort()
                return this.itemsForTable.filter(item => {
                    return this.filterForUnifiedSearch(item)
                })
            }

            if (conditions.length > 0) {
                return this.itemsForTable.filter(item => {
                    return conditions.every(condition => {
                        return condition(item)
                    })
                })
            }
            this.sort()
            return this.itemsForTable
        },
    },
    watch: {
        notificationResource: function(resource) {
            if (resource) {
                this.resourceId = resource
                this.openResource()
                this.setNotificationResource(undefined)
            }
        },
    },
    async created() {
        try {
            this.loading = true
            const {
                data: { users },
            } = await API.getLiteUsers()
            this.users = users
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    async mounted() {
        try {
            this.resourceId = this.$route.query.resourceId
            const {
                data: { user },
            } = await API.getMyInfo()

            this.checkAccountantPurchasesPermission = user.permissions.includes(
                'checkAccountantPurchases'
            )

            this.initializeDefaultDates()
            await this.setupFirebaseListener()
            await this.getClosedPurchases({
                initialDate: this.dates[0],
                finishDate: this.dates[1],
            })
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        }
    },
    beforeDestroy() {
        this.cancelPendingOperations()
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        ...mapActions(['setNotificationResource']),
        ...mapGetters(['getNotificationResource']),
        async openInvoice(invoice, item) {
            await openFile(
                invoice.fileName,
                `${this.companyId}/purchases/${item.requestId}/invoices`
            )
        },
        formatNumber(number) {
            return new Intl.NumberFormat('de-DE').format(
                Math.ceil(number != undefined ? number : 0)
            )
        },
        truncateInvoiceText(text) {
            if (!text || typeof text !== 'string') return text
            const maxLength = 39
            if (text.length <= maxLength) return text
            return text.substring(0, maxLength) + '...'
        },
        filterForId(item) {
            return item.requestNumber.toString().includes(this.idToFilter)
        },
        filterForItems(item) {
            return (
                item.description &&
                item.description
                    .toLowerCase()
                    .includes(this.itemsToFilter.toLowerCase())
            )
        },
        formatDate(seconds) {
            return moment.unix(seconds).format('YYYY-MM-DD')
        },
        closePurchase() {
            this.alertMessage = 'Purchase was successfully Closed'
            this.activateAlert = true
            setTimeout(() => {
                this.activateAlert = false
            }, 1500)
            this.closeRequestForm()
        },
        filterForDeliveryAt(item) {
            return (
                item.deliveryAt &&
                item.deliveryAt
                    .toLowerCase()
                    .includes(this.deliveryParamToFilter.toLowerCase())
            )
        },
        filterForProject(item) {
            return (
                item.project &&
                item.project.name
                    .toLowerCase()
                    .includes(this.projectToFilter.toLowerCase())
            )
        },
        filterForOrder(item) {
            return (
                item.purchaseOrder &&
                item.purchaseOrder.orderNumber &&
                item.purchaseOrder.orderNumber
                    .toLowerCase()
                    .includes(this.orderToFilter.toLowerCase())
            )
        },
        filterForDate(item) {
            const date = this.formatDate(
                item.createdOn.seconds
                    ? item.createdOn.seconds
                    : item.createdOn._seconds
            )
            return date.includes(this.dateToFilter)
        },
        filterForNotes(item) {
            return (
                item.notes &&
                item.notes
                    .toLowerCase()
                    .includes(this.notesToFilter.toLowerCase())
            )
        },
        filterForLocation(item) {
            return (
                item.deliveryLocation &&
                item.deliveryLocation
                    .toLowerCase()
                    .includes(this.locationToFilter.toLowerCase())
            )
        },
        filterForUsers(item) {
            return (
                (item.createdBy.name &&
                    item.createdBy.name
                        .toLowerCase()
                        .includes(this.usersToFilter.toLowerCase())) ||
                (item.projectManager.name &&
                    item.projectManager.name
                        .toLowerCase()
                        .includes(this.usersToFilter.toLowerCase())) ||
                (item.buyer &&
                this.users.find(user => user.id == item.buyer) != undefined
                    ? this.users
                          .find(user => user.id == item.buyer)
                          .name.toLowerCase()
                          .includes(this.usersToFilter.toLowerCase())
                    : false)
            )
        },
        filterForType(item) {
            return (
                item.type &&
                item.type
                    .toLowerCase()
                    .includes(this.typeToFilter.toLowerCase())
            )
        },
        filterForDateRange(item) {
            if (
                !this.dates ||
                this.dates.length !== 2 ||
                !this.dates[0] ||
                !this.dates[1]
            ) {
                return true
            }

            if (!item.createdOn) {
                return false
            }

            const itemDate = item.createdOn.seconds
                ? moment.unix(item.createdOn.seconds).format('YYYY-MM-DD')
                : item.createdOn._seconds
                ? moment.unix(item.createdOn._seconds).format('YYYY-MM-DD')
                : null

            if (!itemDate) {
                return false
            }

            const startDate = this.dates[0]
            const endDate = this.dates[1]

            return itemDate >= startDate && itemDate <= endDate
        },
        filterForSingleDate(item) {
            if (!this.singleDate) {
                return true
            }

            if (!item.createdOn) {
                return false
            }

            const itemDate = item.createdOn.seconds
                ? moment.unix(item.createdOn.seconds).format('YYYY-MM-DD')
                : item.createdOn._seconds
                ? moment.unix(item.createdOn._seconds).format('YYYY-MM-DD')
                : null

            if (!itemDate) {
                return false
            }

            return itemDate === this.singleDate
        },
        filterForSupplier(item) {
            const supplierName = this.getSupplierName(item)
            return (
                supplierName &&
                supplierName
                    .toLowerCase()
                    .includes(this.supplierToFilter.toLowerCase())
            )
        },
        filterForInvoices(item) {
            if (!item.invoices || item.invoices.length === 0) {
                return false
            }
            return item.invoices.some(invoice => {
                // Search by consecutive
                const consecutiveMatch =
                    invoice.consecutive &&
                    invoice.consecutive
                        .toLowerCase()
                        .includes(this.invoicesToFilter.toLowerCase())

                // Search by fileName
                const fileNameMatch =
                    invoice.fileName &&
                    invoice.fileName
                        .toLowerCase()
                        .includes(this.invoicesToFilter.toLowerCase())

                // Search by dueDate
                const orderNumberMatch =
                    invoice.orderNumber &&
                    invoice.orderNumber
                        .toLowerCase()
                        .includes(this.invoicesToFilter.toLowerCase())

                return consecutiveMatch || fileNameMatch || orderNumberMatch
            })
        },
        getFilteredInvoices(item) {
            if (!item.invoices || item.invoices.length === 0) {
                return []
            }

            // If there's no filter, return all invoices
            if (!this.invoicesToFilter || this.invoicesToFilter.trim() === '') {
                return item.invoices
            }

            const filterText = this.invoicesToFilter.toLowerCase().trim()

            // Filter invoices that match the search criteria
            return item.invoices.filter(invoice => {
                const consecutiveMatch =
                    invoice.consecutive &&
                    invoice.consecutive.toLowerCase().includes(filterText)

                const fileNameMatch =
                    invoice.fileName &&
                    invoice.fileName.toLowerCase().includes(filterText)

                const orderNumberMatch =
                    invoice.orderNumber &&
                    invoice.orderNumber.toLowerCase().includes(filterText)

                return consecutiveMatch || fileNameMatch || orderNumberMatch
            })
        },
        filterForIssueDate(item) {
            if (!item.invoices || item.invoices.length === 0) {
                return false
            }
            return item.invoices.some(invoice => {
                if (!invoice.invoicesDate) {
                    return false
                }
                return invoice.invoicesDate
                    .toLowerCase()
                    .includes(this.issueDateToFilter.toLowerCase())
            })
        },
        filterForDueDate(item) {
            if (!item.invoices || item.invoices.length === 0) {
                return false
            }
            return item.invoices.some(invoice => {
                if (!invoice.dueDate) {
                    return false
                }
                return invoice.dueDate
                    .toLowerCase()
                    .includes(this.dueDateToFilter.toLowerCase())
            })
        },

        // ===== UNIFIED SEARCH =====
        /**
         * Validates if the entered text is a date
         * @param {string} text - Text to validate
         * @returns {boolean} - true if it's a date, false otherwise
         */
        isDateInput(text) {
            if (!text || typeof text !== 'string') return false

            const trimmedText = text.trim()

            // Regex to detect common date formats (simplified)
            const datePatterns = [
                /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
                /^\d{2}\/\d{2}\/\d{4}$/, // DD/MM/YYYY
                /^\d{1,2}\/\d{1,2}\/\d{4}$/, // D/M/YYYY
                /^\d{4}\/\d{1,2}\/\d{1,2}$/, // YYYY/M/D
                /^\d{1,2}-\d{1,2}-\d{4}$/, // D-M-YYYY
            ]

            // Check if it matches any date pattern
            if (datePatterns.some(pattern => pattern.test(trimmedText))) {
                return true
            }

            // Check if it contains only numbers and date separators
            const onlyNumbersAndSeparators = /^[\d\-/]+$/.test(trimmedText)
            if (onlyNumbersAndSeparators && trimmedText.length >= 8) {
                // Check if it looks like a date (contains at least 8 characters and only numbers/separators)
                return true
            }

            return false
        },

        /**
         * Unified filter that searches in all specified fields
         * @param {Object} item - Item to filter
         * @returns {boolean} - true if it matches, false otherwise
         */
        filterForUnifiedSearch(item) {
            const searchText = this.unifiedSearch.toLowerCase().trim()

            // Validate that it's not a date
            if (this.isDateInput(this.unifiedSearch)) {
                return false // Don't allow date search in this input
            }

            // Search in all specified fields
            const searchFields = [
                // ID (requestNumber)
                item.requestNumber ? item.requestNumber.toString() : '',

                // Project
                item.project && item.project.name ? item.project.name : '',

                // Item (description)
                item.description ? item.description : '',

                // Order (purchaseOrder.orderNumber)
                item.purchaseOrder && item.purchaseOrder.orderNumber
                    ? item.purchaseOrder.orderNumber
                    : '',

                // Supplier
                this.getSupplierName(item),

                // Invoices (consecutive, fileName and dueDate)
                item.invoices && item.invoices.length > 0
                    ? item.invoices
                          .map(
                              invoice =>
                                  `${invoice.consecutive ||
                                      ''} ${invoice.fileName ||
                                      ''} ${invoice.dueDate || ''}`
                          )
                          .join(' ')
                    : '',
            ]

            // Search for matches in any of the fields
            return searchFields.some(field => {
                if (field && typeof field === 'string') {
                    return field.toLowerCase().includes(searchText)
                }
                return false
            })
        },

        /**
         * Performs unified search
         */
        async performUnifiedSearch() {
            // Validate that it's not a date
            if (this.isDateInput(this.unifiedSearch)) {
                this.alertMessage =
                    'Date searches are not allowed in this search field. Please use the "Date Range" picker on the right for date-based searches.'
                this.activateAlert = true
                setTimeout(() => {
                    this.activateAlert = false
                }, 3000)
                return
            }

            // Activate unified search mode
            this.isUnifiedSearchActive = true

            // Clear individual filters
            this.idToFilter = undefined
            this.projectToFilter = undefined
            this.itemsToFilter = undefined
            this.orderToFilter = undefined
            this.supplierToFilter = undefined
            this.invoicesToFilter = undefined
            this.issueDateToFilter = undefined
            this.dueDateToFilter = undefined

            this.$nextTick(() => {
                const filteredCount = this.filterRequests.length
                if (filteredCount > 0) {
                    this.alertMessage = `Found ${filteredCount} results for "${this.unifiedSearch}"`
                    this.activateAlert = true
                    setTimeout(() => {
                        this.activateAlert = false
                    }, 1500)
                } else {
                    this.alertMessage = `No results found for "${this.unifiedSearch}"`
                    this.activateAlert = true
                    setTimeout(() => {
                        this.activateAlert = false
                    }, 1500)
                }
            })
        },

        /**
         * Clears unified search - data is already loaded from Firebase
         */
        async clearUnifiedSearch() {
            this.unifiedSearch = ''
            this.isUnifiedSearchActive = false
        },

        /**
         * Resets all filters and returns to initial state
         */
        async resetAllFilters() {
            // Clear unified search
            this.unifiedSearch = ''
            this.isUnifiedSearchActive = false

            // Clear individual filters
            this.idToFilter = undefined
            this.projectToFilter = undefined
            this.itemsToFilter = undefined
            this.orderToFilter = undefined
            this.supplierToFilter = undefined
            this.invoicesToFilter = undefined
            this.issueDateToFilter = undefined
            this.dueDateToFilter = undefined
            this.typeToFilter = undefined

            // Reset dates to default values (last 3 months)
            this.initializeDefaultDates()
            this.singleDate = null

            // Close menus if they are open
            this.menu = false
            this.singleDateMenu = false

            // Data is already loaded from Firebase, filters are applied in computed
            this.alertMessage = 'All filters have been reset'
            this.activateAlert = true
            setTimeout(() => {
                this.activateAlert = false
            }, 1500)
        },

        getUser(id) {
            const user = this.users.find(u => u.id == id)
            if (user) {
                return user.name
            }
            return null
        },
        getSupplierName(item) {
            if (!item || !item.quotes || item.quotes.length === 0) {
                return 'No supplier'
            }

            const enabledQuote = item.quotes.find(quote => quote.able === true)

            if (enabledQuote && enabledQuote.supplier) {
                return enabledQuote.supplier.name
            }

            const firstQuote = item.quotes[0]
            if (firstQuote && firstQuote.supplier) {
                return firstQuote.supplier.name
            }

            return 'No supplier'
        },
        openRequest(item) {
            // Search in allPurchases to find both active and closed purchases
            const request = this.allPurchases.find(r => r.id === item.requestId)
            if (request) {
                this.selectedRequest = { ...request }
                this.originalRequest = { ...request }
                this.openForm = true
            }
        },
        closeRequestForm() {
            this.openForm = false
            this.selectedRequest = {}
            this.originalRequest = {}
        },
        replaceRequest() {},
        openResource() {
            if (this.resourceId) {
                const resource = this.allPurchases.find(
                    r => r.id == this.resourceId
                )
                if (resource) {
                    this.resourceId = undefined
                    if (resource.items && resource.items.length > 0) {
                        const firstItem = {
                            ...resource.items[0],
                            requestId: resource.id,
                            requestNumber: resource.number,
                            requestStatus: resource.status,
                            project: resource.project,
                            createdBy: resource.createdBy,
                            projectManager: resource.projectManager,
                            buyer: resource.buyer,
                            accountant: resource.items[0].accountant,
                            accountantChecked:
                                resource.items[0].accountant || false,
                            accountantLoading: false,
                            createdOn: resource.createdOn,
                            dueDate: resource.dueDate,
                            importation: resource.importation,
                            type: resource.type,
                            notes: resource.notes,
                            deliveryLocation: resource.deliveryLocation,
                            totalRequestPrice: this.calculateTotal(resource),
                            sapPercentage: resource.sapPercentage,
                        }
                        this.openRequest(firstItem)
                    }
                }
            }
        },
        getCreatedOnTimestamp(item) {
            return item.createdOn?.seconds || item.createdOn?._seconds || 0
        },
        sort() {
            // Sort function: newest first (descending)
            const sortByDate = (a, b) => {
                const aSeconds = this.getCreatedOnTimestamp(a)
                const bSeconds = this.getCreatedOnTimestamp(b)
                return bSeconds - aSeconds
            }
            // Sort allRequests
            this.allRequests.sort(sortByDate)
            // Sort closedPurchases
            this.closedPurchases.sort(sortByDate)
        },
        calculateTotal(request) {
            let calculatedTotal = 0
            request.items = request.items ? request.items : []
            request.items.forEach(i => {
                i.quotes = i.quotes ? i.quotes : []
                i.quotes.forEach(q => {
                    if (q.able) {
                        calculatedTotal +=
                            Number(i.quantity || 0) *
                            Number(q.priceBeforeTRM ?? q.price ?? 0)
                    }
                })
            })
            return calculatedTotal
        },
        calculateItemTotal(item) {
            let calculatedTotal = 0
            item.quotes = item.quotes ? item.quotes : []
            item.quotes.forEach(q => {
                if (q.able) {
                    calculatedTotal +=
                        Number(item.quantity || 0) *
                        Number(q.priceBeforeTRM ?? q.price ?? 0)
                }
            })
            return calculatedTotal
        },
        getSapPercentage(purchase) {
            try {
                let entriesQty = 0
                let sapQty = 0

                if (purchase?.items?.length > 0) {
                    purchase.items.forEach(item => {
                        if (item.entries && item.entries.length > 0) {
                            item.entries.forEach(entry => {
                                entriesQty++
                                if (entry?.sap === true) {
                                    sapQty++
                                }
                            })
                        }
                    })
                }

                return (entriesQty > 0
                    ? (sapQty / entriesQty) * 100
                    : 0
                ).toFixed(2)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        getTotalReceivedQuantity(item) {
            if (!item?.entries || item?.entries?.length === 0) {
                return 0
            }
            return item.entries.reduce((total, entry) => {
                return total + (entry.quantity || 0)
            }, 0)
        },
        getQuantityStatusColor(item) {
            const qty = item.quantity || 0
            const received = item.totalReceived || 0

            if (received === 0) {
                return 'red'
            } else if (received >= qty) {
                return 'green'
            } else {
                return 'orange'
            }
        },
        // Calculate expected total from item entries
        getItemExpectedTotal(item) {
            if (!item.entries || item.entries.length === 0) {
                return 0
            }
            return item.entries.reduce((total, entry) => {
                return total + (entry.quantity || 0)
            }, 0)
        },
        // Calculate total from related entries in invoices
        getItemRelatedTotal(item) {
            if (!item.requestInvoices || item.requestInvoices.length === 0) {
                return 0
            }

            let totalSelectedQty = 0
            const itemId = item.id

            item.requestInvoices.forEach(invoice => {
                if (
                    invoice.relatedEntries &&
                    invoice.relatedEntries.length > 0
                ) {
                    invoice.relatedEntries.forEach(relatedEntry => {
                        if (relatedEntry.itemId === itemId) {
                            totalSelectedQty += relatedEntry.selectedQty || 0
                        }
                    })
                }
            })

            return totalSelectedQty
        },
        // Check if all entries have SAP = true
        allEntriesHaveSap(item) {
            if (!item.entries || item.entries.length === 0) {
                return false
            }
            return item.entries.every(entry => entry.sap === true)
        },
        // Check if all entries have ACCOUNTANT = true
        allEntriesHaveAccountant(item) {
            if (!item.entries || item.entries.length === 0) {
                return false
            }
            return item.entries.every(entry => entry.accountant === true)
        },
        // Get SAP status for item
        getItemSapStatus(item) {
            const expectedTotal = this.getItemExpectedTotal(item)
            const relatedTotal = this.getItemRelatedTotal(item)

            // Check if totals match
            if (expectedTotal !== relatedTotal) {
                return false
            }

            // Check if all entries have SAP = true
            return this.allEntriesHaveSap(item)
        },
        // Get ACCOUNTANT status for item
        getItemAccountantStatus(item) {
            const expectedTotal = this.getItemExpectedTotal(item)
            const relatedTotal = this.getItemRelatedTotal(item)

            // Check if totals match
            if (expectedTotal !== relatedTotal) {
                return false
            }

            // Check if all entries have ACCOUNTANT = true
            return this.allEntriesHaveAccountant(item)
        },
        // Get status for item (completed or pending)
        getItemStatus(item) {
            const hasSap = this.getItemSapStatus(item)
            const hasAccountant = this.getItemAccountantStatus(item)

            // Both SAP and ACCOUNTANT must be checked for completed status
            if (hasSap && hasAccountant) {
                return 'Completed'
            }
            return 'Pending'
        },

        initializeDefaultDates() {
            const today = new Date()
            const threeMonthsAgo = new Date()
            threeMonthsAgo.setMonth(today.getMonth() - 3)

            this.dates = [
                threeMonthsAgo.toISOString().substr(0, 10),
                today.toISOString().substr(0, 10),
            ]
            // Initialize tempDates with the same values
            this.tempDates = [...this.dates]
        },
        onMenuOpen(isOpen) {
            // When menu opens, initialize tempDates with current dates
            if (isOpen) {
                this.tempDates =
                    this.dates && this.dates.length === 2 ? [...this.dates] : []
            }
        },
        allowedDates(date) {
            // If no dates are selected yet, allow all dates
            if (!this.tempDates || this.tempDates.length === 0) {
                return true
            }

            // Get the first selected date (this is the base date for the 3-month calculation)
            const firstDateStr = Array.isArray(this.tempDates)
                ? this.tempDates[0]
                : this.tempDates

            if (!firstDateStr) {
                return true
            }

            const firstDate = new Date(firstDateStr + 'T00:00:00')
            const currentDate = new Date(date + 'T00:00:00')

            // Calculate the maximum allowed date (3 months = 93 days from first date)
            const maxDate = new Date(firstDate)
            maxDate.setDate(maxDate.getDate() + 93)

            // Calculate the minimum allowed date (1 year before, for flexibility)
            const minDate = new Date(firstDate)
            minDate.setDate(minDate.getDate() - 365)

            return currentDate >= minDate && currentDate <= maxDate
        },
        onDateRangeInput() {
            // This method is called when dates are selected in the picker
            // No action needed, validation is done in computed property
        },
        cancelDateRange() {
            // Reset tempDates to current dates
            this.tempDates =
                this.dates && this.dates.length === 2 ? [...this.dates] : []
            this.menu = false
        },
        async onSingleDateSelected(date) {
            if (!date) {
                return
            }

            // Close the menu
            this.singleDateMenu = false

            // Clear date range when using single date
            this.dates = []

            // Deactivate unified search when using date filter
            this.isUnifiedSearchActive = false

            // Get closed purchases for the selected date
            await this.getClosedPurchases({
                initialDate: date,
                finishDate: date,
            })

            // Show confirmation message
            this.alertMessage = `Single date applied: ${date}`
            this.activateAlert = true
            setTimeout(() => {
                this.activateAlert = false
            }, 1500)
        },
        async confirmDateRange() {
            if (!this.isValidDateRange) {
                this.alertMessage =
                    'Date range must not exceed 3 months. Please select a valid range.'
                this.activateAlert = true
                setTimeout(() => {
                    this.activateAlert = false
                }, 3000)
                return
            }

            // Apply the confirmed dates
            this.dates = [...this.tempDates]
            this.menu = false

            // Clear single date when using date range
            this.singleDate = null

            // Deactivate unified search when loading normal data
            this.isUnifiedSearchActive = false

            // Show confirmation message
            this.alertMessage = `Date range applied: ${this.dates[0]} to ${this.dates[1]}`
            this.activateAlert = true
            setTimeout(() => {
                this.activateAlert = false
            }, 1500)

            // get closed purchases for the date range
            await this.getClosedPurchases({
                initialDate: this.dates[0],
                finishDate: this.dates[1],
            })
        },

        // get closed purchases
        async getClosedPurchases({ initialDate, finishDate }) {
            try {
                this.loading = true
                const purchases = await API.getClosedPurchasesForAccountantHistory(
                    {
                        initialDate,
                        finishDate,
                    }
                )
                this.closedPurchases = purchases || []
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },

        /**
         * Sets up Firebase listener for closed purchases
         */
        async setupFirebaseListener() {
            try {
                this.loading = true
                this.allRequests = []

                if (this.listener) {
                    this.listener()
                    this.listener = null
                }

                const PurchasesQuery = firestore
                    .collection('companies')
                    .doc(this.companyId)
                    .collection('purchases')
                    .where('status', 'in', [
                        'inTransit',
                        'received',
                        'readyToClose',
                    ])

                this.listener = PurchasesQuery.onSnapshot(async docSnapshot => {
                    for (const change of docSnapshot.docChanges()) {
                        const request = {
                            ...change.doc.data(),
                            id: change.doc.id,
                        }

                        const processedRequest = this.processPurchaseData(
                            request
                        )

                        if (change.type === 'added') {
                            if (
                                !this.allRequests.some(r => r.id === request.id)
                            ) {
                                this.allRequests.push(processedRequest)
                            }
                        }

                        if (change.type === 'modified') {
                            const index = this.allRequests.findIndex(
                                r => r.id === request.id
                            )
                            if (index >= 0)
                                this.allRequests.splice(
                                    index,
                                    1,
                                    processedRequest
                                )
                        }

                        if (change.type === 'removed') {
                            const index = this.allRequests.findIndex(
                                r => r.id === request.id
                            )
                            if (index >= 0) this.allRequests.splice(index, 1)
                        }
                    }

                    if (this.initialLoad && this.allRequests.length > 0) {
                        this.initialLoad = false
                    }

                    this.loading = false
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        /**
         * Cancels all pending operations and cleans up resources
         */
        cancelPendingOperations() {
            this.isComponentActive = false

            // Cancelar listener de Firestore
            if (this.listener) {
                this.listener()
                this.listener = null
            }

            // Clear arrays to prevent memory leaks
            this.allRequests = []
            this.closedPurchases = []
        },
        /**
         * Process a single purchase request
         */
        processPurchaseData(request) {
            if (request.items) {
                request.items.forEach(item => {
                    item.accountantChecked = item.accountant || false
                    item.accountantLoading = false
                })
            }

            request.sapPercentage = this.getSapPercentage(request)

            if (request.dueDate && request.dueDate.seconds) {
                request.dueDate = this.formatDate(request.dueDate.seconds)
            }

            return request
        },

        /**
         * EXPORTS FILTERED DATA TO EXCEL
         *
         * This function:
         * 1. Prepares data with additional calculated information
         * 2. Uses ExcelExporter utility to create the file
         * 3. Handles invoice export with dynamic columns
         */
        async exportToExcel() {
            try {
                this.exportLoading = true // Show loading indicator

                // Prepare data for export with additional information
                const prepareItemForExport = item => {
                    return {
                        ...item, // All original item data
                        totalReceived: this.getTotalReceivedQuantity(item), // Received quantity
                        supplierName: this.getSupplierName(item), // Supplier name
                        totalPrice: this.calculateItemTotal(item), // Total item price
                        companyId: this.companyId, // Company ID for links
                        requestId: item.requestId, // Request ID for links
                        accountant: this.getItemAccountantStatus(item),
                        sap: this.getItemSapStatus(item),
                        invoices: this.getFilteredInvoices(item),
                    }
                }

                // Prepare data for export with additional information
                const dataToExport = this.filterRequests.map(
                    prepareItemForExport
                )

                // Export to Excel using the improved utility
                const success = await exportPurchaseDataToExcel(
                    dataToExport,
                    'Purchase_Accountant_History'
                )

                if (success) {
                    this.alertMessage = `Excel successfully exported with ${dataToExport.length} elements`
                    this.activateAlert = true
                    setTimeout(() => {
                        this.activateAlert = false
                    }, 750)
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: `Error exporting to Excel: ${error.message}`,
                })
            } finally {
                this.exportLoading = false // Always remove loading indicator
            }
        },
    },
}
</script>

<style>
.v-data-table {
    border: 1px solid #eeeeee;
}
thead {
    background: #eeeeee;
    position: sticky;
    z-index: 1;
}
.v-input .v-label {
    font-size: 12px;
}
.v-data-table-header th {
    background-color: #eeeeee !important;
}
.v-data-table__wrapper {
    overflow-y: auto;
}
.v-data-table__row {
    height: 64px;
}
.v-data-table__wrapper thead tr {
    position: sticky;
    top: 0;
    z-index: 10;
}

/* Styles for date pickers */
.v-date-picker {
    background: white !important;
    border-radius: 4px;
    box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2),
        0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
}

.v-date-picker-table {
    background: white !important;
}

.v-date-picker-table .v-btn {
    background: transparent !important;
}

.v-date-picker-table .v-btn--active {
    background: #1976d2 !important;
    color: white !important;
}

/* Ensure menu has solid background and correct width */
.v-menu__content {
    background: white !important;
    border-radius: 4px;
    overflow: hidden;
    min-width: 290px !important;
    max-width: 290px !important;
    width: 290px !important;
}

/* Control picker body width */
.v-picker__body {
    width: 290px !important;
    min-width: 290px !important;
    max-width: 290px !important;
}

/* Ensure picker container has correct width */
.v-picker {
    width: 290px !important;
    min-width: 290px !important;
    max-width: 290px !important;
}

/* Reduce date picker input width */
.compact-date-field {
    max-width: 250px !important;
}

/* Styles for unified search */
.unified-search-field {
    width: 100% !important;
    max-width: none !important;
    min-width: 300px !important;
}

.unified-search-field .v-input__control:hover {
    background-color: #eeeeee;
}

/* Ensure perfect alignment between fields */
.compact-date-field,
.unified-search-field {
    height: 40px !important;
}

.compact-date-field .v-input__control,
.unified-search-field .v-input__control {
    height: 40px !important;
}

.compact-date-field .v-input__slot,
.unified-search-field .v-input__slot {
    height: 40px !important;
    align-items: center !important;
}

/* Ensure error messages appear below the search field */
.search-field-container {
    position: relative;
    width: 100%;
    flex: 1;
}

.search-field-container .v-messages {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1;
}

/* Styles for search and reset buttons */
.search-button,
.reset-button {
    margin-left: 8px;
    min-width: 100px;
    height: 40px;
}

.search-button .v-btn__content,
.reset-button .v-btn__content {
    font-size: 14px;
    font-weight: 500;
}

/* Espaciado entre botones */
.search-button {
    margin-right: 4px;
}

/* Invoice row alignment - ensures all invoice rows align across columns */
.invoice-item-row {
    display: flex;
    align-items: center;
    min-height: 28px;
    width: 100%;
}

.invoice-filename {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    text-align: left;
    margin: 0;
    font-size: 12px;
}

.invoice-date {
    text-align: center;
    margin: 0;
    font-size: 12px;
    width: 100%;
}
</style>
